import { useI18n } from '@platform/i18n';
import { Button, EntityPicker, entityPickerLabelsFor, Label, type EntityDirectory } from '@platform/ui';
import { useState } from 'react';
import { browserTimeZone, isTimeZone, knownTimeZones } from './calendar/zones';

/** A stable local directory includes saved aliases even when Intl cannot enumerate them. */
export function timeZoneDirectory(saved: string | null): EntityDirectory<string> {
  const zones = [...new Set([saved, browserTimeZone(), 'UTC', ...knownTimeZones()].filter((zone): zone is string => typeof zone === 'string' && isTimeZone(zone)))].sort();
  return { search: (query, signal, offset) => {
    if (signal.aborted) return Promise.resolve({ items: [], nextOffset: null });
    const matches = zones.filter((zone) => zone.toLowerCase().includes(query.trim().toLowerCase()));
    return Promise.resolve({ items: matches.slice(offset, offset + 50).map((zone) => ({ id: zone, label: zone, identity: zone })), nextOffset: offset + 50 < matches.length ? offset + 50 : null });
  } };
}

/** Search never changes the committed zone. Only selection or explicit reset commits. */
export function TimeZonePicker({ id, label, value, onChange, disabled, onPendingChange, fallbackZone, resetLabel, emptyLabel }: {
  id: string; label: string; value: string | null; onChange: (zone: string | null) => void;
  disabled?: boolean; onPendingChange?: (pending: boolean) => void;
  /** null disables a secondary zone; omitted uses the browser for the display zone. */
  fallbackZone?: string | null; resetLabel?: string; emptyLabel?: string;
}) {
  const { t } = useI18n();
  const [directory] = useState(() => timeZoneDirectory(value));
  const [pending, setPending] = useState(false);
  const [resetVersion, setResetVersion] = useState(0);
  const effectiveZone = value ?? (fallbackZone === undefined ? browserTimeZone() : fallbackZone);
  const commit = (zone: string | null) => { setPending(false); onPendingChange?.(false); onChange(zone); };
  return <div className="flex flex-col gap-2">
    <Label htmlFor={id}>{label}</Label>
    <EntityPicker key={resetVersion} id={id} label={label} directory={directory} labels={entityPickerLabelsFor(t)} disabled={disabled} minQueryLength={0}
      value={!pending && effectiveZone ? { id: effectiveZone, label: effectiveZone, identity: effectiveZone } : null}
      placeholder={t('webmail.calendarUx.settings.zonePlaceholder')} aria-describedby={`${id}-hint`}
      onChange={(next) => { if (next) commit(next.id); else { setPending(true); onPendingChange?.(true); } }} />
    <p id={`${id}-hint`} className="text-xs text-muted-foreground">{pending ? t('webmail.regional.zoneSelectionRequired') : effectiveZone ? t('webmail.regional.zoneCommitted', { zone: effectiveZone }) : emptyLabel}</p>
    <Button type="button" variant="ghost" size="sm" className="self-start" disabled={disabled} onClick={() => { setResetVersion((version) => version + 1); commit(null); }}>{resetLabel ?? t('webmail.regional.useBrowserZone')}</Button>
  </div>;
}
